import React, { useMemo, useRef } from "react";
import {
  Chart,
  Series,
  ArgumentAxis,
  ValueAxis,
  Legend,
  Tooltip,
  CommonSeriesSettings,
  Label,
} from "devextreme-react/chart";
import ChartCard from "components/common/pyramid/ChartCard";

const AmortizationDiscountWaterfall = ({ data, palette }) => {
  const chartRef = useRef(null);

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const chartData = data.map((item) => {
    const name = item.name;
    const lineStart = Number(item.lineStart);
    const lineEnd = Number(item.lineEnd);
    const amort = Number(item.amortization);
    const rem = Number(item.remainingDiscount);

    const split = lineStart + amort;

    const amoFrom = Math.min(lineStart, split);
    const amoTo = Math.max(lineStart, split);

    const remFrom = Math.min(split, lineEnd);
    const remTo = Math.max(split, lineEnd);

    return {
      arg: name,
      amoFrom,
      amoTo,
      remFrom,
      remTo,
      endValue: lineEnd,

      lineStart,
      lineEnd,
      amortization: amort,
      remainingDiscount: rem,
    };
  });

  const customizeTooltip = (arg) => {
    const points = arg.points || [arg];
    const mainPoint =
      points.find((p) => p.seriesName !== "Line") || points[0] || arg;

    const d = mainPoint.point?.data || {};
    const variation = Number(d.lineEnd) - Number(d.lineStart);

    return {
      html: `
        <div style="padding: 10px; min-width: 220px;">
          <div style="font-weight: 700; margin-bottom: 8px;">
            ${arg.argumentText}
          </div>
          <div style="margin-bottom: 4px;">
            <span style="font-weight: 600;">Line start</span> :
            ${formatter.format(d.lineStart ?? 0)}
          </div>
          <div style="margin-bottom: 8px;">
            <span style="font-weight: 600;">Line end</span> :
            ${formatter.format(d.lineEnd ?? 0)}
          </div>
          <div style="margin-bottom: 4px;">
            <span style="display:inline-block;width:14px;height:14px;background-color:${
              palette?.[0]
            };border-radius:2px;margin-right:6px;"></span>
            <span style="font-weight: 600;">Amortization</span> :
            ${formatter.format(d.amortization ?? 0)}
          </div>
          <div style="margin-bottom: 4px;">
            <span style="display:inline-block;width:14px;height:14px;background-color:${
              palette?.[1]
            };border-radius:2px;margin-right:6px;"></span>
            <span style="font-weight: 600;">Remaining discount</span> :
            ${formatter.format(d.remainingDiscount ?? 0)}
          </div>
          <div style="margin-top: 6px; border-top: 1px solid #e0e0e0; padding-top: 6px;">
            <span style="font-weight: 600;">Variation</span> :
            ${formatter.format(variation)}
          </div>
        </div>
      `,
    };
  };

  return (
    <ChartCard
      title="Amortization Discount Waterfall"
      chartRef={chartRef}
      showLabelsOnExport={false}
      palette={palette}
      rawDataForExport={data}
    >
      <div className="p-4">
        <Chart
          dataSource={chartData}
          id="amortizationDiscountWaterfallChart"
          ref={chartRef}
          barGroupPadding={0.5}
        >
          <CommonSeriesSettings argumentField="arg" type="rangebar" />

          <Series
            type="stepline"
            name="Line"
            argumentField="arg"
            valueField="endValue"
            color="#000"
            width={1}
            point={{ visible: false }}
            hoverMode="none"
          />

          <Series
            name="Amortization"
            rangeValue1Field="amoFrom"
            rangeValue2Field="amoTo"
            barOverlapGroup="wf"
            hoverMode="nearestPoint"
            color={palette?.[0]}
          />

          <Series
            name="Remaining discount"
            rangeValue1Field="remFrom"
            rangeValue2Field="remTo"
            barOverlapGroup="wf"
            hoverMode="nearestPoint"
            color={palette?.[1]}
          />

          <ArgumentAxis>
            <Label
              wordWrap="normal"
              textOverflow="none"
              overlappingBehavior="stagger"
            />
          </ArgumentAxis>

          <ValueAxis
            grid={{ visible: true }}
            position="left"
            valueMarginsEnabled={true}
            minValueMargin={0.1}
            maxValueMargin={0.1}
          >
            <Label
              customizeText={(arg) => formatter.format(Math.abs(arg.value))}
            />
          </ValueAxis>

          <Legend
            visible={true}
            horizontalAlignment="center"
            verticalAlignment="bottom"
            customizeItems={(items) =>
              items.filter((item) => item.text !== "Line")
            }
          />

          <Tooltip enabled shared customizeTooltip={customizeTooltip} />
        </Chart>
      </div>
    </ChartCard>
  );
};

export default AmortizationDiscountWaterfall;
